import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

/**
 * Работы графика, которые ждут ответа на вопрос (ВП-04). Связь «работа → вопрос» хранит график (поток 3):
 * привязать и отвязать может тот, кто правит график; на диаграмме такие работы отмечены.
 */

type Work = { id: string; code: string; name: string };

export function QuestionWorks({
  questionId,
  projectId,
  works,
  open,
  onChange,
}: {
  questionId: string;
  projectId: string;
  works: Work[];
  open: boolean;
  onChange: () => void;
}) {
  const [pick, setPick] = useState('');
  const list = useQuery({
    queryKey: ['insp-activities', projectId],
    queryFn: () => api<Work[]>(`/supervision/inspections/activities?projectId=${projectId}`),
    enabled: open,
    retry: false,
  });
  const m = useMutation({
    mutationFn: (b: { activityId: string; remove?: boolean }) =>
      api(`/activities/${b.activityId}/holds`, {
        body: { type: 'psd_question', targetId: questionId, remove: b.remove },
      }),
    onSuccess: (_, b) => {
      setPick('');
      onChange();
      toast(b.remove ? 'Работа отвязана' : 'Работа отмечена на графике как ждущая ответа');
    },
  });
  const free = (list.data ?? []).filter((a) => !works.some((w) => w.id === a.id));
  return (
    <Panel title="Работы ждут ответа">
      {works.length ? (
        <ul className="flex flex-col gap-1 text-[14px]">
          {works.map((w) => (
            <li key={w.id} className="flex flex-wrap items-center gap-2">
              <Link
                to="/schedule/$projectId"
                params={{ projectId }}
                className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
              >
                <span className="num mr-1.5 text-ink-2">{w.code}</span>
                <span className="[overflow-wrap:anywhere]">{w.name}</span>
              </Link>
              {open ? (
                <Button
                  variant="ghost"
                  className="h-11 md:h-8"
                  loading={m.isPending && m.variables?.activityId === w.id}
                  onClick={() => m.mutate({ activityId: w.id, remove: true })}
                >
                  Отвязать
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : open ? (
        <p className="text-[14px] text-ink-2">
          Привяжите работы графика, которые не начать без ответа: на графике они будут отмечены, а срок ответа
          — виден рядом с работой.
        </p>
      ) : (
        <p className="text-[14px] text-ink-2">Работы графика к вопросу не привязаны.</p>
      )}
      {open && free.length ? (
        <div className="mt-3 flex flex-col gap-2 md:flex-row">
          <Select
            aria-label="Работа графика"
            className="h-11 md:h-9 md:flex-1"
            value={pick}
            onChange={(e) => setPick(e.target.value)}
          >
            <option value="">— работа графика —</option>
            {free.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </Select>
          <Button
            className="h-11 md:h-9"
            disabled={!pick}
            loading={m.isPending && !m.variables?.remove}
            onClick={() => m.mutate({ activityId: pick })}
          >
            Привязать
          </Button>
        </div>
      ) : null}
      <ErrorBox error={m.error} className="mt-2" />
    </Panel>
  );
}

/** Отметка напоминаний по сроку ответа (ВП-04) */
export function ReminderBadge({ stage }: { stage: number }) {
  if (stage === 3) return <Badge tone="danger">эскалация ГИП</Badge>;
  if (stage === 2) return <Badge tone="warning">просрочен, поручение ПТО</Badge>;
  if (stage === 1) return <Badge>напомнили</Badge>;
  return null;
}
